import * as React from "react";
import styled from "styled-components";

import tissue from "~/assets/svg/tissue.svg";
import arrowsUp from "~/assets/svg/arrowsUp.svg";
import userOxygen from "~/assets/svg/userOxygen.svg";
import userWinning from "~/assets/svg/userWinning.svg";

export type FlipDataType = {
  imgSrc: string | null;
  description: React.ReactNode;
};

export const Hub_Flipper: React.FC<Record<string, never>> = () => {
  const data: FlipDataType[] = [
    {
      imgSrc: userOxygen,
      description: (
        <>Healthy employees are 3x more productive than unhealthy employees.</>
      ),
    },
    {
      imgSrc: arrowsUp,
      description: (
        <>
          Workplaces who don’t promote health and wellbeing are 4x more likely
          to lose talent within 12 months compared to workplaces that do promote
          health and wellbeing.
        </>
      ),
    },
    {
      imgSrc: tissue,
      description: (
        <>
          Implementing a health and wellbeing program reduces absences due to
          sick leave by 25% on average.
        </>
      ),
    },
    {
      imgSrc: userWinning,
      description: (
        <>
          Employees who work in organisations that actively promote health and
          wellbeing are 3x more likely to regard their workplace as a highly
          desirable or above average place to work.
        </>
      ),
    },
  ];

  const [currentSlide, setCurrentSlide] = React.useState<number>(0);

  const handleAnimation = (dir: -1 | 1) => {
    setCurrentSlide((prev) => (prev + dir + data.length) % data.length);
  };

  return (
    <FlipContainer>
      <IndicatorLeft
        className="btn btn-light border"
        onClick={() => handleAnimation(-1)}
      >
        &#8249;
      </IndicatorLeft>
      {data.map(({ imgSrc, description }, idx) => (
        <FlipInner
          key={idx}
          className="bg-primary"
          active={currentSlide === idx}
        >
          <Contents className="">
            {imgSrc && <img src={imgSrc} alt="" width={80} height={80} />}
            {description && (
              <ContentDescription>{description}</ContentDescription>
            )}
          </Contents>
        </FlipInner>
      ))}
      <IndicatorRight
        className="btn btn-light border"
        onClick={() => handleAnimation(1)}
      >
        &#8250;
      </IndicatorRight>
    </FlipContainer>
  );
};

const FlipContainer = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 20rem;
  max-height: 20rem;
  min-width: 10rem;
  min-height: 10rem;
  &::after {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
`;

const FlipInner = styled.div<{ active: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  transition: 500ms all cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: ${(p) => (p.active ? 1 : 0)};
  transform-style: preserve-3d;
  transform: ${(p) => (p.active ? "rotateY(0deg)" : "rotateY(180deg)")};
`;

const IndicatorLeft = styled.button`
  position: absolute;
  padding: 0 0 0.25rem 0;
  width: 2rem;
  height: 2rem;
  top: calc(50% - 1rem);
  left: -1rem;
  z-index: 1;
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 150ms all cubic-bezier(0.34, 1.15, 0.85, 1);
  opacity: 0.9;
  z-index: 2;
  &:active {
    font-size: 3rem;
    padding: 0 0 0.5rem 0;
  }
`;

const IndicatorRight = styled(IndicatorLeft)`
  left: calc(100% - 1rem);
`;

const Contents = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
`;

const ContentDescription = styled.p`
  display: block;
  margin: 0;
  font-size: 1rem;
`;
